import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CartContext = createContext(null);

const CART_STORAGE_KEY = 'cdp_store_cart';

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const saved = localStorage.getItem(CART_STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Persist cart to localStorage
  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((product, variant, customName = '', customNumber = '', quantity = 1, overrideDescription = null, athleteProfileId = null, teamId = null, kitPieceSelections = null) => {
    setItems(prev => {
      // Include description in cartItemId so e.g. "M+Azul" and "M+Vermelho" are distinct entries
      const descKey = overrideDescription || variant.variantType || variant.size || variant.color || '';
      
      // Hash kit piece selections to ensure different kit configurations stay as separate cart items
      let kitPieceHash = '';
      if (kitPieceSelections && kitPieceSelections.length > 0) {
        // Sort by piece ID and unit index to ensure consistent hashing
        const sortedSelections = [...kitPieceSelections].sort((a, b) => 
          (a.kitPieceId === b.kitPieceId) ? a.unitIndex - b.unitIndex : a.kitPieceId - b.kitPieceId
        );
        kitPieceHash = JSON.stringify(sortedSelections);
      }
      
      const cartItemId = `${variant.id}-${descKey}-${customName || ''}-${customNumber || ''}-${athleteProfileId || ''}-${teamId || ''}-${kitPieceHash}`;
      const existing = prev.find(i => i.cartItemId === cartItemId);
      if (existing) {
        return prev.map(i => i.cartItemId === cartItemId ? { ...i, quantity: i.quantity + quantity } : i);
      }
      return [...prev, {
        cartItemId,
        variantId: variant.id,
        productId: product.id,
        productName: product.name,
        imageUrl: product.imageUrl,
        size: variant.size,
        color: variant.color,
        variantType: variant.variantType,
        // Use the combined description when provided (e.g. "Tamanho: M · Cor: Azul")
        variantDescription: overrideDescription ?? (
          variant.variantType
            ? `${variant.variantType}: ${variant.size || variant.color || ''}`
            : (variant.size || variant.color || null)
        ),
        price: product.memberPrice ?? product.basePrice,
        basePrice: product.basePrice,
        memberPrice: product.memberPrice,
        quantity,
        stockQty: variant.stockQty,
        customName,
        customNumber,
        athleteProfileId: athleteProfileId ? parseInt(athleteProfileId, 10) : null,
        teamId: teamId ? parseInt(teamId, 10) : null,
        kitPieceSelections: kitPieceSelections || null,
      }];
    });
    setIsDrawerOpen(true);
  }, []);

  const removeItem = useCallback((id) => {
    setItems(prev => prev.filter(i => (i.cartItemId ? i.cartItemId !== id : i.variantId !== id)));
  }, []);

  const updateQuantity = useCallback((id, quantity) => {
    if (quantity <= 0) {
      removeItem(id);
      return;
    }
    setItems(prev => prev.map(i =>
      (i.cartItemId ? i.cartItemId === id : i.variantId === id) ? { ...i, quantity } : i
    ));
  }, [removeItem]);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);
  const totalPrice = items.reduce((sum, i) => sum + i.price * i.quantity, 0);

  return (
    <CartContext.Provider value={{
      items,
      totalItems,
      totalPrice,
      isDrawerOpen,
      setIsDrawerOpen,
      addItem,
      removeItem,
      updateQuantity,
      clearCart,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used inside CartProvider');
  return ctx;
}
